import { useLDClient } from '@afterpaytouch/feature'
import { useAppDispatch } from '../hooks'
import { setFeatureFlag } from './reducer'
import { Experiment, Experiments, KnownExperiment } from '../../model'
import { useExperiment } from '../experiments/hooks'
import { useConsumerSelector, useFeatureFlagsState } from '..'

export const useCachedFeatureFlag = <T = boolean>(key: string, defaultValue?: T): T => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const client = useLDClient()
  const cachedValue = flags[key] as T | undefined

  if (!hasFeatureFlagsReady) {
    return defaultValue as T
  }

  if (cachedValue !== undefined) {
    return cachedValue
  }

  const ldVariation = client?.variation(key) as T | undefined

  if (ldVariation !== undefined) {
    dispatch(setFeatureFlag({ key, value: ldVariation }))
    return ldVariation
  }

  return defaultValue as T
}

export const useReadyFeatureFlag = <T = boolean>(key: string, defaultValue: T): T => {
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const client = useLDClient()
  if (!hasFeatureFlagsReady) {
    return defaultValue
  }
  const value = client?.variation(key, defaultValue) as T | undefined
  return value ?? defaultValue
}

export const useCachedExperiment = (name: KnownExperiment, fireEvent: boolean = true): Experiment => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const cachedValue = flags[Experiments[name]] as Experiment | undefined
  const experiment = useExperiment(name, fireEvent)

  if (cachedValue !== undefined) {
    return cachedValue
  }

  if (!experiment.loading) {
    dispatch(setFeatureFlag({ key: Experiments[name], value: experiment }))
  }

  return experiment
}
